import { bindingComponentType } from '@common/constants/shared';
import { useLocation } from '@common/routes/hooks';
import ActionWrapper from '@common/screens/LoadingAction';
import { IOnPressProps } from '@common/types/action';
import { getSourceDataBinding } from '@common/utils/binding';
import {
  checkingTypeComponent,
  getFlattedMetadata,
  memoriesConditional,
} from '@common/utils/component';
import { actionPromise } from '@common/utils/handleActions/excuteAction';
import { find, get, omit } from 'lodash';
import qs from 'query-string';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ListObject from './Components/ListObject';
import SimpleObject from './Components/SimpleObject';
import DependenciesConnector from './DependenciesConnnector';

type Props = {
  ObjectClass: FC;
  obj: any;
  dependencies: any;
};

const isShowLoadingAction = [
  'label',
  'image',
  'section',
  'Icon',
  'ActionButton',
  'Button',
  'CustomButton',
  'LineLogin',
];

const BindingComponent = React.memo(
  ({ ObjectClass, obj, dependencies }: Props) => {
    const { search } = useLocation();
    const query = useMemo(() => qs.parse(search), [search]);

    const { messages } = useIntl();
    const [loading, setLoading] = useState<boolean>(false);

    const onPress = useCallback(
      async (actionId: string | null, options: IOnPressProps) => {
        try {
          const {
            itemListClick = null,
            idUnivaPaySubscription = null,
            record = {},
            formId = null,
            code = '',
            ids = [],
            scannerCode = null,
          } = options || {};

          if (!actionId || typeof actionId !== 'string') return;

          const metadata = getFlattedMetadata(obj.screenUuid);

          const actionObject = find(metadata, (item) =>
            JSON.stringify(item).includes(actionId)
          );

          if (!actionObject) return;

          const objectId = actionObject.id;

          setLoading(true);
          const arrayAction = get(obj, `actions.${actionId}.actions`, []);
          const clientValues = {
            ...dependencies,
            valueInputs: {
              ...dependencies.valueInputs,
              ...(scannerCode && { [obj.id]: scannerCode }),
            },
            currentListIds: {
              ...dependencies?.currentListIds,
              ...((itemListClick && omit(itemListClick, ['recordObj'])) || {}),
            },
            ...(idUnivaPaySubscription && {
              idUnivaPaySubscription: idUnivaPaySubscription,
            }),
          };

          const currentRecordIds = {
            ...obj.currentRecordIds,
            ...((itemListClick && itemListClick?.recordObj) || {}),
          };

          return actionPromise({
            objectId,
            screenUuid: obj?.screenUuid,
            actionId,
            clientValues,
            currentRecordIds,
            messages,
            arrayAction,
            data: {
              ...record,
              ...(code && { code }),
              ...(ids && ids.length > 0 && { ids }),
            },
            ...(formId && {
              formId: formId,
            }),
          })
            .then((data) => {
              setLoading(false);
              return data;
            })
            .catch(() => {});
        } catch (error) {
          console.log('error', error);
          setLoading(false);
        }
      },
      [dependencies, messages, obj, query]
    );

    const dataBinding = getSourceDataBinding(obj);

    const props = useMemo(
      () => ({ ObjectClass, obj, onPress, dependencies }),
      [ObjectClass, obj, onPress, dependencies]
    );

    const componentType =
      checkingTypeComponent(obj) || bindingComponentType.SIMPLE;

    const loadingAction =
      loading && isShowLoadingAction.includes(obj.componentName);

    if (loadingAction) {
      return <ActionWrapper obj={obj} />;
    }

    if (componentType === bindingComponentType.MULTIPLE) {
      return <ListObject {...props} dataBinding={dataBinding} />;
    }

    return <SimpleObject {...props} />;
  },
  memoriesConditional(['obj', 'dependencies', 'ObjectClass', 'screenUuid'])
);

export default DependenciesConnector(BindingComponent);
